import React, { useState, useEffect } from 'react';
import FileUploader from '../../file_uploader/FileUploader';
import { getToken } from '../utils/auth';
import { fetchTaxStatus } from '../api/taxStatus';

import { API_BASE_URL } from '../utils/constants';

/**
 * Tax certificate uploader.
 *
 * Props:
 * - showCard: wrap in card with heading/description (default: true)
 * - showTitle: show heading text when showCard is true (default: true)
 * - showDescription: show description text when showCard is true (default: true)
 *
 * Note: extra props like `title` and `className` are intentionally not
 * forwarded to the DOM so they can be used by parent modals.
 */
function TaxCertUploader({
  showCard = true,
  showTitle = true,
  showDescription = true,
  title = 'Tax Exempt?',
  message = 'Upload your tax exemption certificate here.',
  
}) {
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | uploading | success | error
  const [error, setError] = useState('');

  const [loadingStatus, setLoadingStatus] = useState(true);
  const [isTaxExempt, setIsTaxExempt] = useState(false);

  useEffect(() => {
    const loadTaxStatus = async () => {
      try {
        const { token } = getToken();
        if (!token) {
          setLoadingStatus(false);
          return;
        }

        const response = await fetch(`${API_BASE_URL}/api/customer/tax-status?token=${encodeURIComponent(token)}`);
        if (!response.ok) {
          // If this fails, we still allow upload; just log and continue.
          console.error('Failed to load tax status', await response.text());
          setLoadingStatus(false);
          return;
        }

        const data = await fetchTaxStatus(token);

        // const data = await response.json();
        const alreadyExempt =
          data?.taxExempt === true ||
          data?.hasTaxExemptionUrl === true ||
          data?.hasTaxCertFile === true;

        setIsTaxExempt(alreadyExempt);
        setLoadingStatus(false);
      } catch (err) {
        console.error('Error loading tax status:', err);
        setLoadingStatus(false);
      }
    };

    loadTaxStatus();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!file) {
      setError('Please select a file to upload.');
      return;
    }

    const { token } = getToken();
    if (!token) {
      setError('No authentication token found. Please use the secure link from your email.');
      return;
    }

    try {
      setStatus('uploading');
      const formData = new FormData();
      formData.append('taxCertFile', file);
      formData.append('token', token);

      const response = await fetch('/api/customer/tax-cert', {
        method: 'POST',
        body: formData,
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || 'Failed to upload tax certificate');
      }

      setStatus('success');
    } catch (err) {
      console.error('Tax certificate upload failed:', err);
      setError(err.message || 'Failed to upload tax certificate');
      setStatus('error');
    }
  };

  if (loadingStatus) {
    return null;
  }

  if (isTaxExempt) {
    // Customer already marked tax exempt / has cert on file: hide uploader UI
    return null;
  }

  const TaxCertForm = (
    <form onSubmit={handleSubmit}>
      <FileUploader
        accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
        maxSizeMB={15}
        onFileSelect={setFile}
      />

      {error && <p className="tax-cert-error">{error}</p>}

      <div className="quote-update-line quote-update-button-wrapper">
        <button
          type="submit"
          className="button button--primary button--full"
          disabled={status === 'uploading'}
        >
          {status === 'uploading' ? 'Uploading…' : 'Upload Tax Certificate'}
        </button>
      </div>

      {status === 'success' && !error && (
        <p className="tax-cert-success">
          Your tax certificate has been uploaded successfully. We will review it and get back to you soon.
        </p>
      )}
    </form>
  );

  if (showCard) {
    return (
      <div className="card tax-cert-uploader">
        <div className="card__section">
          {showTitle && title && (
            <h3 className="blog-sidebar__block-title heading h3 tax-cert-title">
              {title}
            </h3>
          )}
          {showDescription && message && (
            <p className="tax-cert-description">
              {message}
            </p>
          )}

          {TaxCertForm}
        </div>
      </div>
    );
  }

  // Minimal version (no outer card or copy, for embedding in modals, etc.)
  return (
    <div className="tax-cert-uploader-no-text">
      {TaxCertForm}
    </div>
  );
}

export default TaxCertUploader;

